import dayjs from 'dayjs';

import {
	DATE_PRECISIONS,
	formatDateByPrecision,
	formatDisplayDateTime,
	formatDisplayTime,
	formatShortDateByPrecision,
	formatShortDateTime,
	parseStoredDate,
	parseStoredTime,
} from './dateDisplay';

/**
 * The three date options of the SECOND pass — Multiple values, Weekdays and
 * Precision (docs/plans/card-editor-dates-groups-filters.md Part 2c) — and
 * the one place every surface asks what they mean. `dateBounds` answers
 * "where may this value sit"; this module answers "how many values are there,
 * which days may they land on, and how coarsely are they read".
 *
 * The wire contract (frozen with the API half), flat under `element.options`:
 *
 *   allowMultiple  true on `date` / `date-time` (never `time`, never with a
 *                  range on either side, and no bound may NAME a multi
 *                  element). The stored value is an ARRAY of the element's
 *                  stored shape, canonical ASCENDING and de-duplicated.
 *                  Readers take a scalar as a one-entry list, and an array
 *                  under an element whose multi is off as its first value —
 *                  turning the option off must not blank a column.
 *   weekdays       number[] on `date` only, 0 = Sunday … 6 (dayjs), non-empty.
 *                  Every entry's calendar weekday has to be in the set; the
 *                  API refuses with 400 `range` { bound: 'weekdays', limit }.
 *   precision      'day' | 'month' | 'year' on `date` only. DISPLAY AND ENTRY
 *                  only: the stored value is always a whole 'YYYY-MM-DD'
 *                  (the first of the month, Jan 1 of the year), which is what
 *                  keeps sorting, KQL, formulas and exports untouched by it.
 *
 * `allowMultiple` is spelled the same as a card lookup's, and means the same
 * thing — more than one value — but the two never mix: `pruneOptionsForType`
 * drops it on any type change that is not date ↔ date-time, because a list of
 * ids is not a list of days and nothing downstream reads them alike.
 */

export const MULTI_DATE_TYPES = ['date', 'date-time'];

/** Does this element hold a LIST of dates? (The option alone is not enough.) */
export const allowsMultipleDates = element => (
	!!element?.options?.allowMultiple && MULTI_DATE_TYPES.includes(element?.dataType)
);

/* ---------------------------------------------------------------------------
 * Weekdays
 * ------------------------------------------------------------------------- */

export const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** The seven-way row's captions. Two of them are "T" and two are "S", which
 *  is why each control also carries the full name as its accessible label. */
export const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const WEEKDAYS_ONLY = [1, 2, 3, 4, 5];

const WEEKEND_ONLY = [0, 6];

const sameSet = (a, b) => a.length === b.length && a.every((value, index) => value === b[index]);

/** A stored `weekdays` as a clean ascending set, or null when it says nothing. */
export function normalizeWeekdays(value) {
	if (!Array.isArray(value)) return null;
	const days = [...new Set(
		value.map(entry => Number(entry)).filter(day => Number.isInteger(day) && day >= 0 && day <= 6)
	)].sort((a, b) => a - b);
	// An EMPTY set is not a rule, it is a field nobody could fill in — the API
	// refuses it and the editor never writes it, so it reads as absent here.
	return days.length ? days : null;
}

/** The weekday rule an element carries, or null. `date` elements only. */
export const weekdaysOf = element => (
	element?.dataType === 'date' ? normalizeWeekdays(element?.options?.weekdays) : null
);

/** Does this moment's calendar weekday sit in the set? (No set = any day.) */
export const isWeekdayAllowed = (moment, weekdays) => {
	const days = normalizeWeekdays(weekdays);
	if (!days || !moment) return true;
	return days.includes(moment.day());
};

/**
 * The set in the words a message uses: "a weekday", "a weekend day", "a
 * Monday or Thursday". The two presets get their own name because that is
 * what people call them — a rule that reads "a Monday, Tuesday, Wednesday,
 * Thursday or Friday" is a rule nobody finishes.
 */
export function describeWeekdays(weekdays) {
	const days = normalizeWeekdays(weekdays);
	if (!days || days.length === 7) return '';
	if (sameSet(days, WEEKDAYS_ONLY)) return 'a weekday';
	if (sameSet(days, WEEKEND_ONLY)) return 'a weekend day';
	const names = days.map(day => WEEKDAY_NAMES[day]);
	if (names.length === 1) return `a ${names[0]}`;
	return `a ${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/* ---------------------------------------------------------------------------
 * Precision
 * ------------------------------------------------------------------------- */

export { DATE_PRECISIONS };

/** 'day' | 'month' | 'year' — 'day' for anything that is not a `date`. */
export const precisionOf = element => (
	(element?.dataType === 'date' && DATE_PRECISIONS.includes(element?.options?.precision))
		? element.options.precision
		: 'day'
);

/* ---------------------------------------------------------------------------
 * Lists of dates
 *
 * Every reader takes BOTH shapes, whatever the option currently says: a
 * scalar under a multi element is a one-entry list, an array under a scalar
 * element is its first value. An author turning the option on or off must
 * never blank a column of stored values.
 * ------------------------------------------------------------------------- */

const isBlankEntry = value => value === null || value === undefined || value === '';

/**
 * One stored entry as a comparable instant. Deliberately NOT
 * `dateBounds.momentOfStored`: `dateBounds` reads the weekday words from
 * here (a 400 `range` names the rule), so this module must not read back —
 * and a list is only ever a list of `date` or `date-time` values, which is
 * the whole of what this has to know.
 */
const momentOfEntry = (mode, value) => {
	if (isBlankEntry(value)) return null;
	if (mode === 'time') return parseStoredTime(value);
	if (mode === 'date') return parseStoredDate(value);
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed : null;
};

/** Any stored date value as a list of entries, blanks dropped. */
export function toDateList(value) {
	if (isBlankEntry(value)) return [];
	if (Array.isArray(value)) return value.filter(entry => !isBlankEntry(entry));
	return [value];
}

/**
 * The canonical stored list: blanks dropped, ASCENDING, de-duplicated by the
 * instant each entry names (so '2026-09-09' and a legacy timestamp on the
 * same day are one date, which is what a reader would say they are).
 * Entries the mode cannot read keep their order at the end rather than being
 * thrown away — a value nobody can parse is still somebody's data.
 */
export function normalizeDateList(mode, value) {
	const readable = [];
	const unreadable = [];
	toDateList(value).forEach(entry => {
		const at = momentOfEntry(mode, entry);
		if (at) readable.push({ entry, at: at.valueOf() });
		else unreadable.push(entry);
	});
	readable.sort((a, b) => a.at - b.at);
	const seen = new Set();
	const out = [];
	readable.forEach(({ entry, at }) => {
		if (seen.has(at)) return;
		seen.add(at);
		out.push(entry);
	});
	unreadable.forEach(entry => {
		if (out.includes(entry)) return;
		out.push(entry);
	});
	return out;
}

/**
 * The ONE value that stands for the whole list wherever the platform needs a
 * single date: the sort key, the primary date, a day group, a tile's header.
 * The EARLIEST entry — the first dose, the first visit, the day the thing
 * began — which is also the first entry of a canonical list.
 */
export function earliestDateValue(mode, value) {
	const list = toDateList(value);
	if (!list.length) return undefined;
	let best;
	let bestAt = null;
	list.forEach(entry => {
		const at = momentOfEntry(mode, entry);
		if (!at) return;
		if (bestAt === null || at.valueOf() < bestAt) {
			bestAt = at.valueOf();
			best = entry;
		}
	});
	return best === undefined ? list[0] : best;
}

/* ---------------------------------------------------------------------------
 * Reading a date value
 * ------------------------------------------------------------------------- */

/** ONE entry in its element's own words — precision included. */
export function formatDateEntry(element, value, { short = false } = {}) {
	if (isBlankEntry(value)) return '';
	const mode = element?.dataType;
	if (mode === 'time') return formatDisplayTime(value);
	if (mode === 'date-time') return short ? formatShortDateTime(value) : formatDisplayDateTime(value);
	const precision = precisionOf(element);
	return short ? formatShortDateByPrecision(value, precision) : formatDateByPrecision(value, precision);
}

/**
 * A whole date value — one entry, or a list joined with ", " in ascending
 * order. The one function every flat display surface (a cell, a template
 * token, a fallback title) calls for a date.
 */
export function formatDateValue(element, value, { short = false } = {}) {
	const list = normalizeDateList(element?.dataType, value);
	if (!list.length) return '';
	return list.map(entry => formatDateEntry(element, entry, { short })).join(', ');
}

/**
 * The COMPACT reading a tile has room for: the earliest entry plus how many
 * more there are — "Sep 9 +2" — with the whole list for the tooltip. Null
 * when there is nothing to show.
 */
export function summarizeDateValues(element, value) {
	const list = normalizeDateList(element?.dataType, value);
	if (!list.length) return null;
	return {
		values: list,
		lead: formatDateEntry(element, list[0], { short: true }),
		extra: list.length - 1,
		all: list.map(entry => formatDateEntry(element, entry)).join(', '),
	};
}
